// Author: Ahsan Saleem
/**
 * BloomOS Intelligence — AI as an operating-system service, not a chatbot.
 *
 * A service that reads the live facility snapshot and surfaces signals: anomalies,
 * offline-sensor predictions, and suggested next actions — each with an HONEST,
 * explainable confidence and a one-click route to the object it's about. It is
 * deliberately deterministic and derived from real data (no fabricated readings,
 * no hallucinated claims), matching the platform's rule that the cloud never
 * asserts what it can't substantiate. The seam is model-ready: `deriveInsights`
 * is a pure function a learned model could later augment or replace.
 *
 * It is woven in as a service, not a page: an Insights Centre in the system bar,
 * an Intelligence section inside object views, and a summary chip on the canvas.
 */
import { useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Sparkles, TriangleAlert, Activity, Lightbulb, ChevronRight } from 'lucide-react';
import { metricSuffix, type FacilitySnapshot, type Latest } from '../api.js';
import { useInspector } from '../ui/Shell.js';
import { useSystem } from '../ui/system.js';
import { openObject } from './objects.js';

export type InsightKind = 'anomaly' | 'prediction' | 'suggestion' | 'summary';
export type InsightSeverity = 'critical' | 'caution' | 'info';
export interface Insight {
  id: string; kind: InsightKind; severity: InsightSeverity;
  title: string; detail?: string; confidence: number; // 0..1, honest
  object?: { kind: string; ref: string; label: string };
}

const fmtAge = (sec: number | null | undefined): string => {
  if (sec == null) return 'a while';
  const h = sec / 3600;
  return h < 1 ? `${Math.max(1, Math.round(sec / 60))} min` : h < 48 ? `${h.toFixed(1)} h` : `${Math.round(h / 24)} d`;
};

/** Pure inference over the live snapshot. Explainable, confidence-scored, honest. */
export function deriveInsights(s: FacilitySnapshot): Insight[] {
  const out: Insight[] = [];
  const zoneName = new Map(s.zones.map((z) => [z.id, z.name]));

  for (const a of s.alerts.filter((x) => x.severity?.toUpperCase() === 'CRITICAL' && x.state?.toUpperCase() !== 'RECOVERED')) {
    out.push({
      id: `alert-${a.id}`, kind: 'suggestion', severity: 'critical',
      title: `Critical: ${metricSuffix(a.metric)} out of band${a.zone_id ? ` in ${zoneName.get(a.zone_id) ?? 'a zone'}` : ''}`,
      detail: `${a.rule_name}${a.opened_value != null ? ` tripped at ${a.opened_value}` : ''}. Corrective action recommended now — the PLC owns any physical interlock.`,
      confidence: 0.92, object: { kind: 'alert', ref: a.id, label: 'Open alert' },
    });
  }

  const stale = s.latest.filter((l) => l.stale);
  const byZone = new Map<string, Latest[]>();
  for (const l of stale) { const k = l.zoneId ?? 'site'; const arr = byZone.get(k) ?? []; arr.push(l); byZone.set(k, arr); }
  const gatewayZones = new Set<string>();
  for (const [zk, ls] of byZone) {
    if (ls.length >= 3 && zk !== 'site') {
      gatewayZones.add(zk);
      out.push({
        id: `gw-${zk}`, kind: 'prediction', severity: 'caution',
        title: `${ls.length} sensors stale in ${zoneName.get(zk) ?? 'a zone'}`,
        detail: 'Several channels stopped reporting together — likelier the zone gateway or its power than every sensor at once. Check the gateway first.',
        confidence: 0.8, object: { kind: 'zone', ref: zk, label: 'Open zone' },
      });
    }
  }
  for (const l of stale.filter((x) => !gatewayZones.has(x.zoneId ?? 'site')).slice(0, 4)) {
    const h = (l.ageSeconds ?? 3600) / 3600;
    out.push({
      id: `stale-${l.datapointCode}`, kind: 'prediction', severity: 'caution',
      title: `${metricSuffix(l.datapointCode)} sensor likely offline`,
      detail: `No reading for ${fmtAge(l.ageSeconds)}${l.zoneId && zoneName.get(l.zoneId) ? ` in ${zoneName.get(l.zoneId)}` : ''}. Check its power and connectivity.`,
      confidence: Math.min(0.95, 0.5 + (h / 24) * 0.4), object: { kind: 'datapoint', ref: l.datapointCode, label: 'Open sensor' },
    });
  }

  const overdue = s.brief?.attention?.overdueTasks ?? 0;
  if (overdue > 0) out.push({
    id: 'overdue', kind: 'suggestion', severity: 'caution',
    title: `${overdue} task${overdue === 1 ? '' : 's'} overdue`,
    detail: 'Overdue corrective/routine work tends to precede an escalation. Clearing the queue is the highest-leverage next action.',
    confidence: 0.7,
  });

  const worst: InsightSeverity = out.some((i) => i.severity === 'critical') ? 'critical' : out.length ? 'caution' : 'info';
  out.unshift({
    id: 'summary', kind: 'summary', severity: worst,
    title: out.length === 0 ? 'All systems within limits' : `${out.length} signal${out.length === 1 ? '' : 's'} to review`,
    detail: `${s.zones.length} zones · ${s.latest.length} sensors · ${s.alerts.length} open alerts. Derived live from this snapshot.`,
    confidence: 1,
  });
  return out;
}

/** Insights that concern one specific object — for the Intelligence section in views. */
export function insightsForObject(kind: string, ref: string, s: FacilitySnapshot): Insight[] {
  const all = deriveInsights(s).filter((i) => i.kind !== 'summary');
  if (kind === 'datapoint') return all.filter((i) => i.object?.kind === 'datapoint' && i.object.ref === ref);
  if (kind === 'alert') return all.filter((i) => i.object?.kind === 'alert' && i.object.ref === ref);
  if (kind === 'zone') return all.filter((i) =>
    (i.object?.kind === 'zone' && i.object.ref === ref)
    || (i.object?.kind === 'datapoint' && s.latest.find((l) => l.datapointCode === i.object!.ref)?.zoneId === ref)
    || (i.object?.kind === 'alert' && s.alerts.find((a) => a.id === i.object!.ref)?.zone_id === ref));
  return [];
}

const KIND_ICON: Record<InsightKind, typeof Sparkles> = { anomaly: TriangleAlert, prediction: Activity, suggestion: Lightbulb, summary: Sparkles };

function Confidence({ v }: { v: number }) {
  const pct = Math.round(v * 100);
  return (
    <span className="ai-conf" aria-label={`${pct}% confidence`}>
      <span className="ai-conf-bar"><span className="ai-conf-fill" style={{ width: `${pct}%` }} /></span>
      <span className="ai-conf-n tnum">{pct}%</span>
    </span>
  );
}

export function InsightList({ insights, onAction }: { insights: Insight[]; onAction?: () => void }) {
  const inspector = useInspector();
  if (!insights.length) return <div className="ai-empty">No signals — everything within limits.</div>;
  return (
    <div className="ai-list">
      {insights.map((i) => {
        const Icon = KIND_ICON[i.kind];
        const body = (
          <>
            <span className={`ai-card-ic tone-${i.severity}`} aria-hidden><Icon size={15} /></span>
            <span className="ai-card-body">
              <span className="ai-card-t">{i.title}</span>
              {i.detail && <span className="ai-card-d">{i.detail}</span>}
              <span className="ai-card-foot">
                <span className="ai-kind">{i.kind}</span>
                <Confidence v={i.confidence} />
                {i.object && <span className="ai-open">{i.object.label}<ChevronRight size={12} aria-hidden /></span>}
              </span>
            </span>
          </>
        );
        return i.object
          ? <button key={i.id} className="ai-card is-open" onClick={() => { openObject(inspector, i.object!.kind, i.object!.ref); onAction?.(); }}>{body}</button>
          : <div key={i.id} className="ai-card">{body}</div>;
      })}
    </div>
  );
}

/** Facility-level intelligence: a compact chip (canvas status bar) or the full list. */
export function FacilityInsights({ snapshot, compact }: { snapshot: FacilitySnapshot; compact?: boolean }) {
  const insights = deriveInsights(snapshot);
  if (!compact) return <InsightList insights={insights} />;
  const top = insights.find((i) => i.severity === 'critical') ?? insights.find((i) => i.severity === 'caution') ?? insights[0];
  const n = insights.filter((i) => i.kind !== 'summary').length;
  return <span className={`ai-chip tone-${top?.severity ?? 'info'}`}><Sparkles size={13} aria-hidden /> {n ? `${n} signal${n === 1 ? '' : 's'}` : 'All nominal'}</span>;
}

/** The Intelligence section woven into an object view. */
export function ObjectInsights({ kind, objectRef, snapshot }: { kind: string; objectRef: string; snapshot?: FacilitySnapshot }) {
  if (!snapshot) return null;
  const insights = insightsForObject(kind, objectRef, snapshot);
  if (!insights.length) return null;
  return (
    <div className="ai-section">
      <div className="ai-section-h"><Sparkles size={13} aria-hidden /> Intelligence</div>
      <InsightList insights={insights} />
    </div>
  );
}

/** Insights Centre — the system-bar presence, alongside Control & Notification. */
export function InsightsCentre() {
  const { snapshot } = useSystem();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener('bloomos-open-insights', on);
    return () => window.removeEventListener('bloomos-open-insights', on);
  }, []);
  const insights = snapshot ? deriveInsights(snapshot) : [];
  const actionable = insights.filter((i) => i.kind !== 'summary');
  const critical = actionable.some((i) => i.severity === 'critical');
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button className={`ai-bell${critical ? ' has-critical' : ''}`} aria-label={`Intelligence — ${actionable.length} signal${actionable.length === 1 ? '' : 's'}`}>
          <Sparkles size={16} aria-hidden />
          {actionable.length > 0 && <span className={`ai-bell-dot${critical ? ' crit' : ''}`} aria-hidden>{actionable.length}</span>}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="centre ai-centre" sideOffset={8} align="end">
          <div className="centre-title"><Sparkles size={14} aria-hidden /> BloomOS Intelligence</div>
          {snapshot ? <InsightList insights={insights} onAction={() => setOpen(false)} /> : <div className="ai-empty">Waiting for data…</div>}
          <div className="ai-foot">Derived live from the facility snapshot · confidence shown per signal</div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
